import Vue, { CreateElement } from 'src/vue'
import { Component, Watch } from 'vue-property-decorator'
import XLSX from 'xlsx'

import { TimeSwitcher } from '@src/elements/time-switch/time-switch'
import { getCurrentWeekPeriods } from '@src/util/functions'
import { Body2, H6, Subtitle1, Subtitle2 } from '@src/elements/typography/typography'
import { Chart } from '@src/components/chart/chart'
import { ErrorScreen } from '@src/components/error-screen/error-screen'
import { Loader } from '@src/components/loading-screen/loader/loader'
import { DateFilterType } from '@src/elements/time-switch/date-filter-type'
import { ClubStatsService } from '@src/service/club-stats'
import StationWorkoutsDay, { StationClassCount, StationClassInfo, StationWorkoutCount } from '@src/data/station-workouts-day'
import WorkoutData from '@src/data/workout-data'
import { convertToLocalDate } from '@src/util/date-format'
import { Workbook } from '@src/util/download-workbook'
import { Card } from '@src/components/card/card'
import { WorkoutCardInfo } from '@src/components/card/workout-card/workout-card-info/workout-card-info'
import { WorkoutRuns } from '@src/components/card/workout-card/workout-runs/workout-runs'
import { Pagination } from '@src/elements/pagination/pagination'
import { Tooltip } from '@src/elements/tooltip/tooltip'
import { Icon } from '@src/elements/icon/icon'
import { DEFAULT_PAGE_SIZE } from '@src/util/constants'

import './station-workouts.sass'

const BAR_COLOR = '#4FACFE'
const CLASS_BAR_COLOR = '#79EDB6'
const UNSELECTED_BAR_COLOR = '#D9DEDD'
const FALLBACK_PREVIEW_IMAGE = '/assets/icon/brand/default-workout/0.jpg'

type StationWorkoutsTab = 'workouts' | 'classes'

// What an empty tab says, by tab and by selected day or whole period
const NO_RUNS_KEYS: { [tab in StationWorkoutsTab]: { day: string, period: string } } = {
  workouts: {
    day: 'PAGE.CLUB.CLUB_STATISTICS.STATION_WORKOUTS_NO_WORKOUT_RUNS_DAY',
    period: 'PAGE.CLUB.CLUB_STATISTICS.STATION_WORKOUTS_NO_WORKOUT_RUNS_PERIOD'
  },
  classes: {
    day: 'PAGE.CLUB.CLUB_STATISTICS.STATION_WORKOUTS_NO_CLASS_RUNS_DAY',
    period: 'PAGE.CLUB.CLUB_STATISTICS.STATION_WORKOUTS_NO_CLASS_RUNS_PERIOD'
  }
}

type Selection = { entries: StationWorkoutCount[], classes: StationClassCount[], workoutRuns: number, classRuns: number, from: string, to: string }

// Same order as the backend: most runs first, unknown ones last, then by name
function compareCounts (a: { count: number, name: string | null, id: number | null }, b: { count: number, name: string | null, id: number | null }) {
  if (a.count !== b.count) return b.count - a.count
  if (!a.name !== !b.name) return a.name ? -1 : 1
  const byName = (a.name || '').toLowerCase().localeCompare((b.name || '').toLowerCase())
  if (byName !== 0) return byName
  return (a.id || 0) - (b.id || 0)
}

function compareWorkoutCounts (a: StationWorkoutCount, b: StationWorkoutCount) {
  return compareCounts({ count: a.count, name: a.workoutName, id: a.workoutId }, { count: b.count, name: b.workoutName, id: b.workoutId })
}

function compareClassCounts (a: StationClassCount, b: StationClassCount) {
  return compareCounts({ count: a.count, name: a.className, id: a.interactiveClassId }, { count: b.count, name: b.className, id: b.interactiveClassId })
}

@Component({
  computed: {
    locale () {
      return this.$store.state.i18n.locale
    }
  }
})
export class StationWorkoutsStatistics extends Vue {

  private clubStatsService: ClubStatsService = new ClubStatsService()

  private days: StationWorkoutsDay[] = []
  private workouts: { [id: number]: WorkoutData } = {}
  private periodWorkouts: StationWorkoutCount[] = []
  private periodClasses: StationClassCount[] = []
  private classInfos: { [id: number]: StationClassInfo } = {}
  private activeTab: StationWorkoutsTab = 'workouts'
  private selectedDate: string | null = null
  private currentPage: number = 1
  private pageSize: number = DEFAULT_PAGE_SIZE
  private series: any[] = []

  private loading: boolean = true
  private failed: boolean = false
  private lastRequest: number = 0

  changeTimeFrame (timeFrame: string, periods: any[]) {
    return this.loadStationWorkouts(periods)
  }

  // A class bar (second series) opens the classes, a workout bar the workouts; the other bar of the
  // selected day only switches the tab
  selectDay (index: number, seriesIndex: number = 0) {
    const day = this.days[index]
    if (!day || day.total + day.classCount === 0) return
    const tab: StationWorkoutsTab = seriesIndex === 1 ? 'classes' : 'workouts'
    if (this.selectedDate === day.date && tab !== this.activeTab) {
      this.activeTab = tab
    } else {
      this.selectedDate = this.selectedDate === day.date ? null : day.date
      if (this.selectedDate) this.activeTab = tab
    }
    this.currentPage = 1
    this.series = this.getChartSeries()
  }

  selectTab (tab: StationWorkoutsTab) {
    this.activeTab = tab
    this.currentPage = 1
  }

  // The pagination is below the cards: show the list from its top instead of the end of the page
  changePage (page: number) {
    this.currentPage = page
    this.$nextTick(() => (this.$refs.selectedDay as HTMLElement).scrollIntoView({ block: 'start' }))
  }

  @Watch('locale')
  refreshSeries () {
    this.series = this.getChartSeries()
  }

  getSelectedDay () {
    return this.days.find(day => day.date === this.selectedDate)
  }

  hasRuns () {
    return this.days.some(day => day.total > 0)
  }

  hasClasses () {
    return this.days.some(day => day.classCount > 0)
  }

  // The tabs are only shown when the period has classes
  showClassesTab () {
    return this.hasClasses() && this.activeTab === 'classes'
  }

  // The workouts and classes of the selected day, or of the whole period without a selected day;
  // workout runs and class runs are counted apart
  getSelection (): Selection {
    const day = this.getSelectedDay()
    if (day) return { entries: day.workouts, classes: day.classes, workoutRuns: day.total, classRuns: day.classCount, from: day.date, to: day.date }

    return {
      entries: this.periodWorkouts,
      classes: this.periodClasses,
      workoutRuns: this.days.reduce((sum, periodDay) => sum + periodDay.total, 0),
      classRuns: this.days.reduce((sum, periodDay) => sum + periodDay.classCount, 0),
      from: this.days[0].date,
      to: this.days[this.days.length - 1].date
    }
  }

  getWorkoutName (entry: StationWorkoutCount) {
    const workout = typeof entry.workoutId === 'number' ? this.workouts[entry.workoutId] : undefined
    return (workout && workout.name) || entry.workoutName || this.$t('PAGE.CLUB.CLUB_STATISTICS.STATION_WORKOUTS_UNKNOWN') as string
  }

  getClassName (entry: StationClassCount) {
    return entry.className || this.$t('PAGE.CLUB.CLUB_STATISTICS.STATION_WORKOUTS_UNKNOWN_CLASS') as string
  }

  // All workouts of the selection, not only the ones of the current page, with their total, then its classes
  // under their own header row with theirs; the same file whichever tab is open
  getDownloadRows ({ entries, classes, workoutRuns, classRuns }: Selection) {
    const classColumn = this.$t('PAGE.CLUB.CLUB_STATISTICS.STATION_WORKOUTS_COLUMN_CLASS') as string
    const classRunsColumn = this.$t('PAGE.CLUB.CLUB_STATISTICS.STATION_WORKOUTS_CLASS_RUNS') as string
    const totalRuns = this.$t('PAGE.CLUB.CLUB_STATISTICS.STATION_WORKOUTS_TOTAL_RUNS') as string
    const hasWorkouts = entries.length > 0
    const firstColumn = hasWorkouts ? this.$t('PAGE.CLUB.CLUB_STATISTICS.STATION_WORKOUTS_COLUMN_WORKOUT') as string : classColumn
    const runsColumn = hasWorkouts ? this.$t('PAGE.CLUB.CLUB_STATISTICS.STATION_WORKOUTS_WORKOUT_RUNS') as string : classRunsColumn

    const workoutRows = hasWorkouts ? [
      ...entries.map(entry => ({ [firstColumn]: this.getWorkoutName(entry), [runsColumn]: entry.count })),
      { [firstColumn]: totalRuns, [runsColumn]: workoutRuns }
    ] : []
    const classHeader = hasWorkouts ? [{ [firstColumn]: classColumn, [runsColumn]: classRunsColumn }] : []
    const classRows = classes.length > 0 ? [
      ...classHeader,
      ...classes.map(entry => ({ [firstColumn]: this.getClassName(entry), [runsColumn]: entry.count })),
      { [firstColumn]: totalRuns, [runsColumn]: classRuns }
    ] : []

    return [...workoutRows, ...classRows]
  }

  getDownloadName ({ from, to }: Selection) {
    const period = from === to ? from : `${from}_${to}`
    return `${this.$t('PAGE.CLUB.CLUB_STATISTICS.STATION_WORKOUTS')}_${period}`
  }

  downloadList () {
    const selection = this.getSelection()
    const workSheet = XLSX.utils.json_to_sheet(this.getDownloadRows(selection))
    workSheet['!cols'] = [{ width: 50 }, { width: 10 }]
    const workBook = XLSX.utils.book_new()
    XLSX.utils.book_append_sheet(workBook, workSheet, `${this.$t('WORKOUTS')}`)
    try {
      Workbook.download(workBook, `${this.getDownloadName(selection)}.xlsx`)
    } catch (e) {
      this.$store.dispatch('showToast', { message: this.$t('ERRORS.API.UNKNOWN'), fadeOut: false })
    }
  }

  // The workout runs of each day, and next to them its classes when the period has any
  getChartSeries () {
    if (!this.hasRuns() && !this.hasClasses()) return []

    const series = [{
      name: this.$t('PAGE.CLUB.CLUB_STATISTICS.STATION_WORKOUTS_WORKOUT_RUNS'),
      color: BAR_COLOR,
      data: this.days.map(day => ({
        x: new Date(day.date).getTime(),
        y: day.total,
        fillColor: this.getBarColor(day, BAR_COLOR)
      }))
    }]
    if (!this.hasClasses()) return series

    return [...series, {
      name: this.$t('PAGE.CLUB.CLUB_STATISTICS.STATION_WORKOUTS_CLASS_RUNS'),
      color: CLASS_BAR_COLOR,
      data: this.days.map(day => ({
        x: new Date(day.date).getTime(),
        y: day.classCount,
        fillColor: this.getBarColor(day, CLASS_BAR_COLOR)
      }))
    }]
  }

  // With a selected day the bars of all other days are grey
  getBarColor (day: StationWorkoutsDay, color: string) {
    return !this.selectedDate || day.date === this.selectedDate ? color : UNSELECTED_BAR_COLOR
  }

  renderLegend (h: CreateElement) {
    if (this.series.length < 2) return null

    return <div class='row station-workouts-legend'>
      {this.series.map(item => <div class='row legend-item' key={item.color}>
        <span class='legend-color' style={{ backgroundColor: item.color }} />
        <Body2 class='legend-name'>{item.name}</Body2>
      </div>)}
    </div>
  }

  renderChart (h: CreateElement) {
    if (this.loading) return <Loader />
    if (this.failed) return <ErrorScreen icon='brand/empty-graph' message={this.$t('ERRORS.API.UNKNOWN')} />

    return [
      this.renderLegend(h),
      <Chart chartId='stationWorkoutsChart' type='bar' series={this.series}
             grid={{ padding: { left: 30, right: 30, bottom: 20 } }}
             labelStyle={{ style: { color: '#212121', fontSize: '0.5rem' } }}
             emptyGraphMessage={this.$t('PAGE.CLUB.CLUB_STATISTICS.STATION_WORKOUTS_EMPTY')}
             dateType='day'
             pointSelect={this.selectDay}
      />
    ]
  }

  renderTabs (h: CreateElement) {
    if (!this.hasClasses()) return null

    const tabs: { tab: StationWorkoutsTab, label: string }[] = [
      { tab: 'workouts', label: this.$t('WORKOUTS') as string },
      { tab: 'classes', label: this.$t('PAGE.CLUB.CLUB_STATISTICS.STATION_WORKOUTS_CLASSES') as string }
    ]

    return <div class='row station-workouts-tabs' role='tablist'>
      {tabs.map(({ tab, label }) => <button type='button' key={tab} role='tab' aria-selected={String(this.activeTab === tab)}
                                            class={{ 'station-workouts-tab': true, selected: this.activeTab === tab }}
                                            onClick={() => this.selectTab(tab)}>
        {label}
      </button>)}
    </div>
  }

  // Without a selected day the workouts or classes of the whole period are shown
  renderSelectedDay (h: CreateElement) {
    if (this.loading || this.series.length === 0) return null

    const day = this.getSelectedDay()
    const selection = this.getSelection()
    const label = day ? convertToLocalDate(day.date) : `${convertToLocalDate(selection.from)} – ${convertToLocalDate(selection.to)}`
    const keyPrefix = day ? day.date : 'period'
    const showClasses = this.showClassesTab()
    const items: any[] = showClasses ? selection.classes : selection.entries
    const pageStart = (this.currentPage - 1) * this.pageSize

    return <div class='selected-day' ref='selectedDay'>
      {day ?
        <Body2 class='unselect-day-hint'>{this.$t('PAGE.CLUB.CLUB_STATISTICS.STATION_WORKOUTS_UNSELECT_DAY')}</Body2> :
        <Body2 class='select-day-hint'>{this.$t('PAGE.CLUB.CLUB_STATISTICS.STATION_WORKOUTS_SELECT_DAY')}</Body2>}
      {this.renderTabs(h)}
      <div class='row selected-day-summary'>
        <Subtitle2 class='selected-date'>{label}</Subtitle2>
        <div class='row selected-totals'>
          <Subtitle2 class='selected-total-runs'>
            {this.$t('PAGE.CLUB.CLUB_STATISTICS.STATION_WORKOUTS_TOTAL_RUNS')}: {showClasses ? selection.classRuns : selection.workoutRuns}
          </Subtitle2>
          {showClasses ?
            <Subtitle2 class='selected-total-classes'>{this.$t('PAGE.CLUB.CLUB_STATISTICS.STATION_WORKOUTS_TOTAL_CLASSES')}: {items.length}</Subtitle2> :
            <Subtitle2 class='selected-total-workouts'>{this.$t('PAGE.CLUB.CLUB_STATISTICS.STATION_WORKOUTS_TOTAL_WORKOUTS')}: {items.length}</Subtitle2>}
          {this.renderDownload(h, selection)}
        </div>
      </div>
      <div class='workouts'>
        {items.length === 0 ?
          <Body2 class='station-workouts-no-runs'>{this.$t(NO_RUNS_KEYS[showClasses ? 'classes' : 'workouts'][day ? 'day' : 'period'])}</Body2> :
          this.paginate(items).map((entry, index) => showClasses ?
            this.renderClass(h, entry, `${keyPrefix}-class-${pageStart + index}`) :
            this.renderWorkout(h, entry, `${keyPrefix}-${pageStart + index}`))}
      </div>
      {items.length > this.pageSize ?
        <Pagination changePage={this.changePage}
                    currentPage={this.currentPage}
                    pageSize={this.pageSize}
                    total={items.length} /> : null}
    </div>
  }

  renderDownload (h: CreateElement, selection: Selection) {
    const label = this.$t('PAGE.CLUB.CLUB_STATISTICS.STATION_WORKOUTS_DOWNLOAD') as string

    // iOS does not save files written by XLSX, so the list is downloaded as csv there
    if (/iPad|iPhone|iPod/.test(navigator.userAgent || navigator.vendor)) {
      return <vue-json-to-csv json-data={this.getDownloadRows(selection)} csv-title={this.getDownloadName(selection)}>
        <button type='button' class='download-list' aria-label={label}>
          <Icon name='util/download' />
        </button>
      </vue-json-to-csv>
    }

    return <Tooltip content={label}>
      <button type='button' class='download-list' aria-label={label} ref='downloadList' onClick={this.downloadList}>
        <Icon name='util/download' />
      </button>
    </Tooltip>
  }

  renderWorkout (h: CreateElement, entry: StationWorkoutCount, key: string) {
    const hasId = typeof entry.workoutId === 'number'
    const workout = hasId ? this.workouts[entry.workoutId as number] : undefined
    if (workout) {
      return <WorkoutCardInfo class='station-workout' key={key} type='stats' workout={workout} runs={entry.count} />
    }

    const preview = hasId ? `/assets/icon/brand/default-workout/${(entry.workoutId as number) % 30}.jpg` : FALLBACK_PREVIEW_IMAGE
    return <Card class='workout-card station-workout station-workout-fallback' key={key}>
      <WorkoutRuns slot='leading' runs={entry.count} />
      <div slot='preview' class='workout-preview-big'>
        <img src={preview} alt='' />
      </div>
      <H6 slot='title' class='title'>{entry.workoutName || this.$t('PAGE.CLUB.CLUB_STATISTICS.STATION_WORKOUTS_UNKNOWN')}</H6>
    </Card>
  }

  // The class's own picture, else the default class picture chosen by its id
  renderClass (h: CreateElement, entry: StationClassCount, key: string) {
    const hasId = typeof entry.interactiveClassId === 'number'
    const info = hasId ? this.classInfos[entry.interactiveClassId as number] : undefined
    const defaultPreview = hasId ? `/assets/icon/brand/default-workout/${(entry.interactiveClassId as number) % 30}.jpg` : FALLBACK_PREVIEW_IMAGE
    const preview = (info && info.previewImage) || defaultPreview

    return <Card class='workout-card station-workout station-class' key={key}>
      <WorkoutRuns slot='leading' runs={entry.count} />
      <div slot='preview' class='workout-preview-big'>
        <img src={preview} alt='' />
      </div>
      <H6 slot='title' class='title'>{this.getClassName(entry)}</H6>
    </Card>
  }

  render (h: CreateElement) {
    return <div class='column station-workouts'>
      <Subtitle1>{this.$t('PAGE.CLUB.CLUB_STATISTICS.STATION_WORKOUTS')}</Subtitle1>

      <TimeSwitcher dateFilters={[DateFilterType.WEEK, DateFilterType.MONTH]} change={this.changeTimeFrame} align='right' allowFutureDate={false} />

      <div id='stationWorkoutsChartWrapper'>
        {this.renderChart(h)}
      </div>

      {this.renderSelectedDay(h)}

      {/* separates the section from the next one, below the cards or the pagination */}
      <div class='station-workouts-end' />
    </div>
  }

  beforeMount () {
    return this.loadStationWorkouts(getCurrentWeekPeriods())
  }

  private loadStationWorkouts (periods: any[]) {
    const request = ++this.lastRequest
    const dates: string[] = periods.map(period => period.from)

    this.loading = true
    this.selectedDate = null
    this.currentPage = 1

    return this.clubStatsService.getStationWorkouts(dates[0], periods[periods.length - 1].to)
      .then(({ days, workouts, classDays, classes }) => {
        if (request !== this.lastRequest) return
        this.days = dates.map(date => {
          const day = days.find(periodDay => periodDay.date === date) || new StationWorkoutsDay({ date })
          const classDay = (classDays || []).find(periodClassDay => periodClassDay.date === date)
          return classDay ? new StationWorkoutsDay({ ...day, classCount: classDay.count, classes: classDay.classes }) : day
        })
        this.workouts = (workouts || []).reduce((map: { [id: number]: WorkoutData }, workout) => {
          if (typeof workout.id === 'number') map[workout.id] = workout
          return map
        }, {})
        this.periodWorkouts = this.getPeriodWorkouts()
        this.periodClasses = this.getPeriodClasses()
        this.classInfos = (classes || []).reduce((map: { [id: number]: StationClassInfo }, info) => {
          map[info.id] = info
          return map
        }, {})
        this.failed = false
      })
      .catch(() => {
        if (request !== this.lastRequest) return
        this.days = []
        this.workouts = {}
        this.periodWorkouts = []
        this.periodClasses = []
        this.classInfos = {}
        this.failed = true
        this.$store.dispatch('showToast', { message: this.$t('ERRORS.API.UNKNOWN'), fadeOut: false })
      })
      .then(() => {
        if (request !== this.lastRequest) return
        this.activeTab = this.hasClasses() && !this.hasRuns() ? 'classes' : 'workouts'
        this.series = this.getChartSeries()
        this.loading = false
      })
  }

  private paginate<T> (items: T[]) {
    const start = (this.currentPage - 1) * this.pageSize
    return items.slice(start, start + this.pageSize)
  }

  // Runs of each workout summed over all days of the period
  private getPeriodWorkouts () {
    const totals: { [key: string]: StationWorkoutCount } = {}

    this.days.forEach(day => day.workouts.forEach(entry => {
      const key = typeof entry.workoutId === 'number' ? String(entry.workoutId) : 'unknown'
      const total = totals[key] || (totals[key] = { workoutId: typeof entry.workoutId === 'number' ? entry.workoutId : null, workoutName: null, count: 0 })
      total.count += entry.count
      total.workoutName = total.workoutName || entry.workoutName
    }))

    return Object.keys(totals).map(key => totals[key]).sort(compareWorkoutCounts)
  }

  // Plays of each class summed over all days of the period
  private getPeriodClasses () {
    const totals: { [key: string]: StationClassCount } = {}

    this.days.forEach(day => day.classes.forEach(entry => {
      const hasId = typeof entry.interactiveClassId === 'number'
      const key = hasId ? String(entry.interactiveClassId) : 'unknown'
      const total = totals[key] || (totals[key] = { interactiveClassId: hasId ? entry.interactiveClassId : null, className: null, count: 0 })
      total.count += entry.count
      total.className = total.className || entry.className
    }))

    return Object.keys(totals).map(key => totals[key]).sort(compareClassCounts)
  }
}
